/**
 * Contacts, tags, invitations and organization members — the Phase 1 Contacts page
 * (`chorus-byggeplan.md` Fase 1.1; `docs/design/00-contracts.md` §12.5, §12.6, §13.3).
 *
 * Four families of DTO, one page:
 *
 * * **Contact** — a person as *this* organization knows them. `contacts` is the org-local row;
 *   `persons` is the human. The register holds own staff and externals alike, because there is
 *   no `is_external` column: "external" means "no live membership" (§6 `contacts`).
 * * **Tag** — org-defined labels, DATA and not an enum (principle 9). They exist here because
 *   the list filters by them.
 * * **Invitation** — an invitation for a contact to become a *user*. The person already exists
 *   by the time one is written, so accepting can never duplicate them.
 * * **OrganizationMember** — the Organization tab: the same contact rows filtered by membership
 *   and enriched with org role, responsibility and the simple "reports to" hierarchy.
 *
 * Two contract decisions worth stating out loud, because they are forced rather than chosen:
 *
 * 1. **`email` is required to create a contact.** `contacts.person_id` is `NOT NULL`, and
 *    `app.resolve_person()` — the only code path allowed to insert into `persons` (§6.27) —
 *    refuses without an address. A contact without an email would therefore need a second way
 *    to mint a person, which is exactly the door that guarantees "a person exists once".
 * 2. **The contact note is an ordinary field** (decision 0008). It carries no permission of its
 *    own: "ett felt med egne regler er en regel folk glemmer". Its purpose is defined in the UI
 *    help text instead — gasjer og helseinfo hører på performancen.
 */
import { z } from 'zod';

import { orgRoleSchema } from '../enums.js';
import { localeSchema } from '../locale.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { booleanQuerySchema } from './common.js';
import { rowViewerSchema } from './row-viewer.js';
import { myConnectionDtoSchema } from './person-connections.js';
import { shareKindSchema, shareObjectTypeSchema, sharePermissionSchema } from './shares.js';
import { wireInstant } from './wire-time.js';

/** A repeated (`?tagIds=a&tagIds=b`) or comma-separated (`?tagIds=a,b`) list of uuids. */
const uuidListQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',');
}, z.array(z.uuid()).min(1).max(20));

/**
 * Free text, matched against name, email, company, **job title and phone** through the trigram
 * index (principle 6). The phone half is matched on digits alone, on both sides, so `91234567`
 * and `+47 912 34 567` find each other — migration 0007 explains how, and why it has to happen
 * inside `app.search_contact_ids` rather than here.
 */
const searchQuerySchema = z.string().trim().min(1).max(120);

/**
 * The sort keys `GET /v1/contacts` offers. The first is the default, and every one of them is
 * backed by an index that already orders exactly what the keyset cursor compares:
 *
 * * `name`    → `ix_contacts__org_name`         (organization_id, display_name COLLATE nb, id)
 * * `company` → `ix_contacts__org_company_name` (organization_id, CASE … END COLLATE nb,
 *              display_name COLLATE nb, id) — a company is shared, so the name breaks the tie
 *              before the id does, or a company sort reads as a shuffled list (migration 0008)
 * * `created` → `ix_contacts__org_id_desc` or `ux_contacts__org_id`, both (organization_id, id)
 *              — ids are UUIDv7, so `id` **is** creation order and needs no second column
 *
 * There is deliberately no `email`, `phone` or `jobTitle` sort: none of them has an index, and a
 * sort the database has to materialise is exactly what principle 6 exists to prevent. Adding one
 * starts with a migration, not with a string in this array.
 */
export const CONTACT_SORT_KEYS = ['name', 'company', 'created'] as const;
export type ContactSortKey = (typeof CONTACT_SORT_KEYS)[number];

/**
 * The sort keys `GET /v1/organization/members` offers.
 *
 * `role` is not the same rows in a different order: it drives the query from `org_memberships`
 * rather than from `contacts`, because that is where the index is
 * (`ix_org_memberships__org_role`). Its keyset tiebreaker is therefore `person_id` and not the
 * contact id — which is safe precisely because a cursor records the sort it was minted for and
 * `decodeCursor` refuses it under any other.
 */
export const ORGANIZATION_MEMBER_SORT_KEYS = ['name', 'role'] as const;
export type OrganizationMemberSortKey = (typeof ORGANIZATION_MEMBER_SORT_KEYS)[number];

const displayNameSchema = z.string().trim().min(1).max(200);
const shortTextSchema = z.string().trim().max(200);
const phoneSchema = z.string().trim().max(40);
const notesSchema = z.string().max(10_000);
const emailSchema = z.email().max(320);

/**
 * PRIVAT ELLER DELT (A2, migrering 1021). 'private': bare den som la inn kontakten ser den.
 * 'organization': alle i organisasjonen. Alt fra før 1021 er 'organization' (267.1 a).
 */
export const CONTACT_VISIBILITIES = ['private', 'organization'] as const;
export type ContactVisibility = (typeof CONTACT_VISIBILITIES)[number];
export const contactVisibilitySchema = z
  .enum(CONTACT_VISIBILITIES)
  .meta({ id: 'ContactVisibility' });

/**
 * HVOR RADEN I KONTAKTER KOMMER FRA (A2, én liste). `organization`: organisasjonens kontakt;
 * `private`: min private kontakt her; `colleague`/`connection`: en kollega eller min kobling som ikke
 * står i lista her; `private_elsewhere`: min private kontakt fra en annen organisasjon (267.3 a).
 */
export const CONTACT_ROW_SOURCES = [
  'organization',
  'private',
  'colleague',
  'connection',
  'private_elsewhere',
  /* 1086 (persontypene §2.4): et medlem hos en samarbeidspartner, så lenge samarbeidet varer. */
  'partner',
] as const;
export type ContactRowSource = (typeof CONTACT_ROW_SOURCES)[number];
const colorSchema = z.string().regex(/^#[0-9a-f]{6}$/);

// ---------------------------------------------------------------------------- tags
export const tagDtoSchema = z
  .object({
    id: z.uuid(),
    /** Stable slug generated from the name; seeds and the Phase 2 clone address tags by it. */
    key: z.string(),
    name: z.string(),
    color: colorSchema,
  })
  .meta({ id: 'Tag' });

export const listTagsQuerySchema = cursorQuerySchema(['name']).meta({ id: 'ListTagsQuery' });

export const tagListResponseSchema = page(tagDtoSchema, 'TagListResponse');

export const createTagRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    /** Omitted: the neutral default of `tags.color`. */
    color: colorSchema.optional(),
  })
  .meta({ id: 'CreateTagRequest' });

/**
 * Renaming a tag changes what people read, never what code addresses: `key` is the stable slug
 * the seed, the Phase 2 edition clone and every import speak, so it is deliberately absent here.
 * A tag that needs a different key is a different tag.
 */
export const updateTagRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    color: colorSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateTagRequest' });

/**
 * What `DELETE /v1/tags/:id` answers with, and why it answers at all rather than returning 204.
 *
 * Deleting a tag that eleven contacts carry is not a detail the caller should have to discover
 * afterwards, so the count of affected contacts comes back with the deletion. It is a **soft**
 * delete: the `contact_tags` rows are left exactly where they are, so re-creating the tag with
 * the same name restores the tag row *and* every attachment it had — which is what makes the
 * operation reversible without a restore verb nobody has a permission for.
 */
export const deleteTagResponseSchema = z
  .object({
    tag: tagDtoSchema,
    /** How many contacts carried this tag at the moment it was removed. */
    contactCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'DeleteTagResponse' });

// ---------------------------------------------------------------------------- contacts
/**
 * One Tvitvi house the person behind a contact is a member of (QA 22. september, funn 1).
 *
 * Name and id and nothing else: the membership is the other house's row, and migration 0360's
 * `app.contact_organizations` is the one door it comes through.
 */
export const contactOrganizationDtoSchema = z
  .object({ id: z.uuid(), name: z.string() })
  .meta({ id: 'ContactOrganization' });

export const contactSummaryDtoSchema = z
  .object({
    id: z.uuid(),
    /** The human. Stable across organizations — this is what "one person, once" means. */
    personId: z.uuid(),
    displayName: z.string(),
    jobTitle: z.string().nullable(),
    /**
     * The firm as text, which every contact has, linked or not.
     *
     * When `companyId` is set this is a COPY of that firm's name, written by the one path that
     * links a contact and rewritten when the firm is renamed (`ContactCompaniesService`). The copy
     * is what keeps `ix_contacts__org_company_name` — and therefore the company sort — usable; a
     * `coalesce(firm.name, contacts.company_name)` in the ORDER BY would be an expression no index
     * matches, and the sort would silently become a materialised one.
     */
    companyName: z.string().nullable(),
    /** Set when the firm is a row somebody can open, null when it is only text (migrering 0088). */
    companyId: z.uuid().nullable(),
    /**
     * «ORGANISASJON» SLIK SKJERMEN VISER DEN: husene mennesket er med i, ikke `companyName`.
     *
     * Leserens eget hus først, deretter alfabetisk — så en i Standing Ovation ser «Standing
     * Ovation» først og en i God Nok ser «God Nok». Tom når personen ikke har sagt ja til dette
     * huset (medlem her, eller godtatt kobling), og tom for en rolle uten `membership:read`: hvem
     * som er med i huset er det den rollen er skjermet fra.
     */
    organizations: z.array(contactOrganizationDtoSchema),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    /** Resolved in the same query as the row (principle 6: no N+1). Never a second round trip. */
    tags: z.array(tagDtoSchema),
    /**
     * Om raden står i kontaktregisteret (K-14, migrering 0315).
     *
     * Lista returnerer bare `true` — den ER registeret — så feltet tjener panelet man åpner en
     * frivillig i: det er dét som avgjør om «Løft til kontakt» skal stå der.
     */
    inRegister: z.boolean(),
    /*
     * PERSONEN HAR INNLOGGING (29.09). Da er telefonen i lista brukerens egen (se PHONE_FROM_THE_PERSON
     * i API-et), og en telefon skrevet på kontaktraden ville aldri blitt vist — cella er grå.
     * Kontaktkortet får det samme flagget (217.2): «Send melding» bare til den som kan lese den.
     * Valgfri så eldre testoppsett som bygger raden for hånd fortsatt gjelder.
     */
    hasLogin: z.boolean().optional(),
    /*
     * NOTATET OG SPRÅKET I LISTA (tabellene, bølge 3): «Kolonner» skal kunne vise alt kontakten har.
     * Begge står på detaljsiden for den samme `contact:read`, og notatet er et vanlig felt (vedtak
     * 0008). Valgfrie i typen bare fordi eldre testoppsett bygger raden for hånd; `toContactSummaryDto`
     * sender dem alltid. Hele notatet, aldri avkortet: hurtigredigering skriver tilbake det cella har.
     */
    notes: z.string().nullable().optional(),
    /**
     * Hva leseren kan gjøre med raden (D1, eksempel 22). API-et sender den alltid; valgfri bare for
     * testoppsett som bygger raden for hånd.
     */
    viewer: rowViewerSchema.optional(),
    preferredLocale: localeSchema.nullable().optional(),
    /**
     * KILDEN (253.16): raden er organisasjonens, og er personen i tillegg min personlige kobling,
     * står den med begge — «Organisasjonen · Min kontakt». Valgfri for testoppsett som bygger raden
     * for hånd; API-et sender den alltid.
     */
    isMine: z.boolean().optional(),
    /** A2: privat (bare jeg) eller delt med organisasjonen. Valgfri for testoppsett. */
    visibility: contactVisibilitySchema.optional(),
    /** A2: jeg eier raden, og kan dele den eller gjøre den privat igjen. */
    ownedByMe: z.boolean().optional(),
    /**
     * A2, den sammenslåtte lista (`everyone`): hvor raden kommer fra. Er den ikke `organization` eller
     * `private`, er `id` personens id (raden er ikke en kontakt her) og `opensAs` sier hva et klikk åpner.
     */
    source: z.enum(CONTACT_ROW_SOURCES).optional(),
    /**
     * Personen er medlem av organisasjonen jeg står i (A3). Lista sender det også til en rolle uten
     * `membership:read`, der `organizations` er tom, så kilden sier «Kollega» for alle (klikkrunde 3).
     */
    isColleague: z.boolean().optional(),
    /** `contact`: kontaktsiden (`contactId`); `person`: personkortet; `private`: radens egne felt. */
    opensAs: z.enum(['contact', 'person', 'private']).optional(),
    /** Kontaktraden i organisasjonen jeg står i, når raden er en person som har en (utenfor Kontakter). */
    contactId: z.uuid().nullable().optional(),
    /** For `private_elsewhere`: organisasjonen den private kontakten ble lagt inn i. */
    fromOrganizationName: z.string().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'ContactSummary' });

/** The org-role half of the Organization tab, as the detail page shows it. */
export const contactMembershipDtoSchema = z
  .object({
    membershipId: z.uuid(),
    role: orgRoleSchema,
    jobTitle: z.string().nullable(),
    /** "Ansvarsområde" — free text, because it is org data (principle 9). */
    responsibility: z.string().nullable(),
    reportsToMembershipId: z.uuid().nullable(),
    joinedAt: z.iso.datetime(),
  })
  .meta({ id: 'ContactMembership' });

/**
 * Den ventende invitasjonen, slik kontaktsiden trenger den.
 *
 * Smalere enn `invitationDtoSchema` med vilje: `contactId` og `personId` ville vært den samme
 * raden man står på, gjentatt. Tokenet er IKKE her og kan ikke bli det — det finnes bare i det
 * ene svaret som opprettet invitasjonen (`invitations.token_hash` er revoked for `chorus_app`).
 * «Kopier lenke» må derfor gå via «Send på nytt», som minter et nytt token.
 */
export const openInvitationDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null for `artist` og `external` (0460): de gir ikke et medlemskap. */
    role: orgRoleSchema.nullable(),
    /**
     * «INVITER SOM ARTIST» ER EN ANNEN INVITASJON (QA 26. september, K4). Den gir ikke medlemskap,
     * så `role` er ubrukt og vises ikke, og den trekkes tilbake bak `artist:invite` — cella som
     * sendte den — og ikke bak `invitation:delete`. `external` (sak 232) er den samme formen: en
     * personlig konto, ingen rolle.
     */
    kind: z.enum(['member', 'artist', 'external']).default('member'),
    email: z.email(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'OpenInvitation' });

/** Én levende deling til kontakten, på kontaktkortet (§5.4). */
export const contactShareDtoSchema = z
  .object({
    grantId: z.uuid(),
    kind: shareKindSchema,
    objectType: shareObjectTypeSchema,
    objectId: z.uuid(),
    title: z.string(),
    permission: sharePermissionSchema,
    expiresAt: wireInstant.nullable(),
  })
  .meta({ id: 'ContactShare' });
export type ContactShareDto = z.infer<typeof contactShareDtoSchema>;

export const contactDtoSchema = contactSummaryDtoSchema
  .extend({
    /** Decision 0008: an ordinary field, with no permission logic of its own. */
    notes: z.string().nullable(),
    preferredLocale: localeSchema.nullable(),
    /** null when this contact is not a member of the organization — most of the register. */
    membership: contactMembershipDtoSchema.nullable(),
    /**
     * DEN ÅPNE INVITASJONEN SELV, IKKE EN BOOLEAN (QA12 4a).
     *
     * Feltet het `hasOpenInvitation: boolean`, og da kunne merket «INVITERT» stå i toppen mens
     * tilgangskortet under sa «hen har ikke tilgang og må inviteres» — to sanne setninger om den
     * samme raden, som leste som en selvmotsigelse. Atle ba om at kortet viser den ventende
     * invitasjonen: rolle, gyldig til, og handlingene.
     *
     * Detaljspørringen joinet allerede `invitations` for å regne ut boolen, så dette koster ingen
     * ekstra rundtur — det er de samme radene, projisert framfor talt.
     */
    openInvitation: openInvitationDtoSchema.nullable(),
    /**
     * «Koble til en Tvitvi-bruker» (QA12 #8), sett fra huset som spurte.
     *
     * `pending` er «Forespørsel sendt» — og det står også når ingen på plattformen har adressen,
     * fordi 8b krever at svaret ikke røper det. `accepted` betyr at navn, telefon og e-post er
     * personens egne og ikke kan skrives her. Null når kontakten er manuell.
     */
    link: z.object({ status: z.enum(['pending', 'accepted']) }).nullable(),
    /**
     * MIN PERSONLIGE KOBLING TIL PERSONEN (253.16, 14, 17): «Send kontaktforespørsel», «Venter på
     * svar» eller «Fjern fra mine kontakter». Valgfri for testoppsett; API-et sender den alltid.
     */
    connection: myConnectionDtoSchema.optional(),
    /**
     * Kan jeg be om en kobling: personen har konto og er ikke meg (eller kontakten har e-post), og vi
     * er ikke kolleger (A3).
     */
    canRequestConnection: z.boolean().optional(),
    /** Vi har et aktivt medlemskap i samme organisasjon: kontakter uten forespørsel (A3, 0998). */
    isColleague: z.boolean().optional(),
    /**
     * FORNAVN OG ETTERNAVN PÅ PERSONEN (260.28, 0964). `displayName` er de to satt sammen. Null for en
     * person laget før delene fulgte med; skjemaet foreslår da en deling. Valgfri for testoppsett.
     */
    givenName: z.string().nullable().optional(),
    familyName: z.string().nullable().optional(),
    /**
     * DET ORGANISASJONEN HAR DELT MED PERSONEN NÅ (§5.4, 27.10). Kortet sa «har ikke tilgang til Tvitvi»
     * om en med innlogging og en levende deling. Levende delinger, eksterne og lenker; bare for den som
     * kan invitere eksterne eller se delingsoversikten. Utelatt ellers, og for et medlem.
     */
    shares: z.array(contactShareDtoSchema).optional(),
  })
  .meta({ id: 'Contact' });

/**
 * Hva `DELETE /v1/contacts/:id` gjorde (sak 207).
 *
 * `removed_from_register`: personen har en påmelding i huset, og kontaktraden er stedet der navnet
 * og e-posten til påmeldingen står (0315). Raden blir derfor stående, bare ute av kontaktlista
 * (`inRegister = false`), og Angre er `PATCH inRegister: true`. `deleted`: myk sletting som før,
 * og Angre er `POST …/restore`.
 */
export const DELETE_CONTACT_OUTCOMES = ['deleted', 'removed_from_register'] as const;
export const deleteContactResponseSchema = z
  .object({ outcome: z.enum(DELETE_CONTACT_OUTCOMES) })
  .meta({ id: 'DeleteContactResponse' });

export const listContactsQuerySchema = cursorQuerySchema(CONTACT_SORT_KEYS)
  .extend({
    search: searchQuerySchema.optional(),
    /**
     * OR, not AND: a contact matches if it carries **any** of the tags asked for.
     *
     * Atle: *"Om jeg merker flere tags oppfører den seg som en And funksjon og ikke or."* Two
     * chips in a filter bar read as "crew or volunteers", not as "people who are both", and a
     * second chip should widen the list rather than empty it.
     */
    tagIds: uuidListQuerySchema.optional(),
    /**
     * «Hvem jobber i dette firmaet» — the company page's own list, served by the endpoint that
     * already pages, sorts and applies the `limited` reader's department scope, rather than by a
     * second one that would have to repeat all three. Backed by `ix_contacts__company`.
     */
    companyId: z.uuid().optional(),
    /**
     * A2: ÉN LISTE. Med `everyone` slår lista sammen organisasjonens kontakter, mine private, mine
     * koblinger og kollegene. Uten står den som før (registeret), som velgerne leser.
     */
    everyone: booleanQuerySchema.optional(),
  })
  .meta({ id: 'ListContactsQuery' });

export const contactListResponseSchema = page(contactSummaryDtoSchema, 'ContactListResponse');

/**
 * The two free-text fields that go wrong when everybody types them from memory: "Live Nation",
 * "Live nation", "LiveNation AS". Atle: *"Tittel og selskap bør gi forslag når man trykker på
 * feltet slik at man ikke skriver selskapsnavnet forskjellig med en feil."*
 *
 * It is a **suggestion list, not a controlled vocabulary**. `createContactRequest` and
 * `updateContactRequest` are unchanged and still accept any short text: the first contact at a
 * new company must be able to name it. This endpoint only shows what the organization already
 * says, so the second one can agree with the first.
 */
export const CONTACT_SUGGESTION_FIELDS = ['jobTitle', 'companyName'] as const;
export type ContactSuggestionField = (typeof CONTACT_SUGGESTION_FIELDS)[number];

export const contactSuggestionFieldSchema = z
  .enum(CONTACT_SUGGESTION_FIELDS)
  .meta({ id: 'ContactSuggestionField' });

/** The cap. Never a page: a suggestion list that needs a second page is not a suggestion list. */
export const MAX_CONTACT_SUGGESTIONS = 20;

export const contactSuggestionsQuerySchema = z
  .object({
    field: contactSuggestionFieldSchema,
    /** Prefix, matched case-insensitively. Absent means "the most common openings", capped. */
    prefix: z.string().trim().max(120).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(MAX_CONTACT_SUGGESTIONS)
      .default(MAX_CONTACT_SUGGESTIONS),
  })
  .meta({ id: 'ContactSuggestionsQuery' });

export const contactSuggestionDtoSchema = z
  .object({ value: z.string() })
  .meta({ id: 'ContactSuggestion' });

/**
 * No `nextCursor`, no `hasMore`, no total: this is not a list endpoint wearing a hat. It is a
 * capped set of distinct values, and the client that wants more types another letter.
 */
export const contactSuggestionsResponseSchema = z
  .object({
    field: contactSuggestionFieldSchema,
    items: z.array(contactSuggestionDtoSchema),
  })
  .meta({ id: 'ContactSuggestionsResponse' });

/**
 * «Koble til en Tvitvi-bruker» — det andre valgkortet i «Ny kontakt» (QA12 #8).
 *
 * Bare navn og adresse, fordi det er alt huset skal vite før personen har sagt ja. Navnet er
 * husets eget til da, og det er dit kontakten faller tilbake hvis koblingen trekkes. Adressen slås
 * opp EKSAKT; et fritt navnesøk i alle brukere er det 8b forbyr.
 */
export const createLinkedContactRequestSchema = z
  .object({
    /**
     * VALGFRITT (13, 253.18): «Ny kontakt» krever bare e-post. Uten navn står adressen som navn i
     * registeret til noen skriver noe annet; den som spurte ser personens eget navn når hen godtar.
     */
    displayName: z.string().trim().min(2).max(200).optional(),
    email: z.email().max(320),
  })
  .meta({ id: 'CreateLinkedContactRequest' });

/**
 * FORNAVN OG ETTERNAVN (260.28). Sendes de, setter serveren `displayName` sammen av dem, og delene lagres
 * på personen (0964). `displayName` alene virker som før, for klienter som ikke har to felt.
 */
const nameParts = {
  givenName: z.string().trim().max(100).nullish(),
  familyName: z.string().trim().max(100).nullish(),
};

export const createContactRequestSchema = z
  .object({
    displayName: displayNameSchema,
    ...nameParts,
    /**
     * OPTIONAL SINCE 1. SEPTEMBER, and the reason is worth keeping.
     *
     * It used to be required because `app.resolve_person()` is the only path into `persons` and it
     * raises without an address. Atle: «man skal kunne legge til en ny person kun med navn» — a
     * touring party has a backline tech whose address nobody has, and a required field there is a
     * field somebody fills with `noreply@`. A placeholder in `person_emails` is worse than none:
     * the next four people who invent the same address inherit the same identity.
     *
     * With an address the contact is RESOLVED — the same human across organisations, invitations
     * and logins, which is what the whole `persons` table is for. Without one it is CREATED, and
     * two contacts with the same name are two people, because nothing here could say otherwise.
     * `app.create_person_named()` carries the argument in full.
     */
    email: emailSchema.nullish(),
    jobTitle: shortTextSchema.nullish(),
    companyName: shortTextSchema.nullish(),
    /**
     * Linking to a firm WINS over the text: given both, `companyName` is overwritten with the
     * firm's name. Two fields that can disagree is a bug waiting for somebody to rename the firm,
     * and the row somebody can open is the one that is right.
     */
    companyId: z.uuid().nullish(),
    phone: phoneSchema.nullish(),
    notes: notesSchema.nullish(),
    preferredLocale: localeSchema.nullish(),
    tagIds: z.array(z.uuid()).max(20).optional(),
    /**
     * A2: «Del kontakten med hele organisasjonen». Skjemaet sender `private` som standard (av). Uten
     * feltet blir kontakten organisasjonens, som før, så importer og eldre klienter ikke endrer seg.
     */
    visibility: contactVisibilitySchema.optional(),
  })
  .meta({ id: 'CreateContactRequest' });

export const updateContactRequestSchema = z
  .object({
    displayName: displayNameSchema.optional(),
    ...nameParts,
    /**
     * The organization's preferred address for this person, not the identity key: changing it
     * never re-resolves or re-points `person_id` (§6 `contacts.email`).
     */
    email: emailSchema.nullish(),
    jobTitle: shortTextSchema.nullish(),
    companyName: shortTextSchema.nullish(),
    /** As on create: the link wins, and `null` unlinks without clearing the text. */
    companyId: z.uuid().nullish(),
    phone: phoneSchema.nullish(),
    notes: notesSchema.nullish(),
    preferredLocale: localeSchema.nullish(),
    /** The complete set. Absent leaves the tags alone; `[]` clears them. */
    tagIds: z.array(z.uuid()).max(20).optional(),
    /**
     * «LØFT TIL KONTAKT» (QA13 runde 2, #23 / K-14), som et felt og ikke som en egen rute.
     *
     * Atle: «Man kan løfte en frivillig til kontakt manuelt.» Det er én boolsk opplysning om
     * raden, den krever `contact:update` som alt annet her, og den revideres av
     * `AuditedRepository` uten en linje ekstra. En `POST /contacts/:id/promote` ville vært en
     * andre skrivevei til den samme kolonnen.
     *
     * Nedgraderingen er også mulig — feltet tar `false` — fordi en kontakt som ble løftet ved en
     * feil ellers ikke kunne legges tilbake uten å slettes.
     */
    inRegister: z.boolean().optional(),
    /** A2: bare eieren kan dele en privat kontakt, eller gjøre sin egen kontakt privat igjen. */
    visibility: contactVisibilitySchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateContactRequest' });

// ---------------------------------------------------------------------------- invitations
export const invitationDtoSchema = z
  .object({
    id: z.uuid(),
    contactId: z.uuid(),
    personId: z.uuid(),
    /** A snapshot of the address invited; the person's addresses may change afterwards. */
    email: z.email(),
    /** Null for en artist- eller ekstern invitasjon (0460). */
    role: orgRoleSchema.nullable(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    invitedByPersonId: z.uuid().nullable(),
  })
  .meta({ id: 'Invitation' });

export const inviteContactRequestSchema = z
  .object({
    /**
     * Subject to decision 0007 in `can()`: nobody invites at or above their own level, and
     * `superadmin` is the only exception. Refused with `cannot_grant_higher_role`.
     */
    role: orgRoleSchema,
    jobTitle: shortTextSchema.nullish(),
    responsibility: shortTextSchema.nullish(),
    /* Hvem personen rapporterer til — et MEDLEMSKAP i dette huset (QA12 #7). */
    reportsToMembershipId: z.uuid().nullish(),
    /** Send invitasjonen på SMS også (jobb 3, 0495). Se `sendSms` i `CreateShareRequest`. */
    sendSms: z.boolean().optional(),
  })
  .meta({ id: 'InviteContactRequest' });

// ---------------------------------------------------------------------------- org members
export const organizationMemberDtoSchema = z
  .object({
    /** The `org_memberships` id — what `PATCH /v1/organization/members/:id` addresses. */
    id: z.uuid(),
    contactId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    role: orgRoleSchema,
    jobTitle: z.string().nullable(),
    responsibility: z.string().nullable(),
    reportsToMembershipId: z.uuid().nullable(),
    joinedAt: z.iso.datetime(),
    /**
     * Null for a member who may still sign in to this house (QA15 blokk B2).
     *
     * A deactivated membership is not deleted: the row, the contact and everything pointing at
     * the membership stay exactly where they are, and `POST :id/reactivate` puts the person back.
     * Removal is the other door, and it soft-deletes the row.
     */
    deactivatedAt: z.iso.datetime().nullable(),
    /** Hva leseren kan gjøre med medlemmet (D1). Rollen låses når leseren ikke kan endre den. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'OrganizationMember' });

/**
 * «Aktive» eller «Deaktiverte» — the one filter the members list offers (QA15 blokk B2).
 *
 * Two values and not three: a REMOVED membership is soft-deleted and is not listed at all, so an
 * «Alle» that could not show it would be a lie in the form of an option.
 */
export const ORGANIZATION_MEMBER_STATUSES = ['active', 'deactivated'] as const;
export type OrganizationMemberStatus = (typeof ORGANIZATION_MEMBER_STATUSES)[number];

export const listOrganizationMembersQuerySchema = cursorQuerySchema(ORGANIZATION_MEMBER_SORT_KEYS)
  .extend({
    search: searchQuerySchema.optional(),
    /* Defaulted rather than optional: the list must never silently mix the two populations. */
    status: z.enum(ORGANIZATION_MEMBER_STATUSES).default('active'),
    /** F53: bare medlemmer med denne rollen. */
    role: orgRoleSchema.optional(),
    /**
     * 253.69: «Plattformadminer» — bare dem, og bare i Tvitvi-organisasjonen. I et annet hus gir
     * filteret en tom liste: hvem som er plattformadmin, er ikke kundens opplysning.
     */
    platformAdmins: z.enum(['true']).optional(),
  })
  .meta({ id: 'ListOrganizationMembersQuery' });

export const organizationMemberListResponseSchema = page(
  organizationMemberDtoSchema,
  'OrganizationMemberListResponse',
);

export const updateOrganizationMemberRequestSchema = z
  .object({
    role: orgRoleSchema.optional(),
    jobTitle: shortTextSchema.nullish(),
    responsibility: shortTextSchema.nullish(),
    /** The simple hierarchy the plan asks for — a field, not an org chart. */
    reportsToMembershipId: z.uuid().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateOrganizationMemberRequest' });

/**
 * What `DELETE /v1/organization/members/:id` answers with, rather than 204 (QA15 blokk B2).
 *
 * The same argument as `DeleteTagResponse`: the caller has just moved somebody else's open work
 * onto their own desk, and finding that out afterwards is worse than being told. `member` is the
 * row as it now stands so the list can be corrected without a refetch racing the toast.
 */
export const removeOrganizationMemberResponseSchema = z
  .object({
    member: organizationMemberDtoSchema,
    /** Open tasks — `status` neither `done` nor `cancelled` — that changed owner with them. */
    reassignedTaskCount: z.number().int().nonnegative(),
    /** Direct reports whose «rapporterer til» was cleared, because it pointed at a tombstone. */
    clearedReportsToCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'RemoveOrganizationMemberResponse' });

export type TagDto = z.infer<typeof tagDtoSchema>;
export type ListTagsQuery = z.infer<typeof listTagsQuerySchema>;
export type TagListResponse = z.infer<typeof tagListResponseSchema>;
export type CreateTagRequest = z.infer<typeof createTagRequestSchema>;
export type UpdateTagRequest = z.infer<typeof updateTagRequestSchema>;
export type DeleteTagResponse = z.infer<typeof deleteTagResponseSchema>;

export type ContactSummaryDto = z.infer<typeof contactSummaryDtoSchema>;
export type ContactMembershipDto = z.infer<typeof contactMembershipDtoSchema>;
export type ContactOrganizationDto = z.infer<typeof contactOrganizationDtoSchema>;
export type OpenInvitationDto = z.infer<typeof openInvitationDtoSchema>;
export type ContactDto = z.infer<typeof contactDtoSchema>;
export type DeleteContactResponse = z.infer<typeof deleteContactResponseSchema>;
export type ListContactsQuery = z.infer<typeof listContactsQuerySchema>;
export type ContactListResponse = z.infer<typeof contactListResponseSchema>;
export type CreateContactRequest = z.infer<typeof createContactRequestSchema>;
export type CreateLinkedContactRequest = z.infer<typeof createLinkedContactRequestSchema>;
export type UpdateContactRequest = z.infer<typeof updateContactRequestSchema>;
export type ContactSuggestionDto = z.infer<typeof contactSuggestionDtoSchema>;
export type ContactSuggestionsQuery = z.infer<typeof contactSuggestionsQuerySchema>;
export type ContactSuggestionsResponse = z.infer<typeof contactSuggestionsResponseSchema>;

export type InvitationDto = z.infer<typeof invitationDtoSchema>;
export type InviteContactRequest = z.infer<typeof inviteContactRequestSchema>;

export type OrganizationMemberDto = z.infer<typeof organizationMemberDtoSchema>;
export type ListOrganizationMembersQuery = z.infer<typeof listOrganizationMembersQuerySchema>;
export type RemoveOrganizationMemberResponse = z.infer<
  typeof removeOrganizationMemberResponseSchema
>;
export type OrganizationMemberListResponse = z.infer<typeof organizationMemberListResponseSchema>;
export type UpdateOrganizationMemberRequest = z.infer<typeof updateOrganizationMemberRequestSchema>;
